import { useEffect } from "react";
import ReactDOM from "react-dom";
import { useSelector, useDispatch } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import { HiCheckCircle, HiExclamationTriangle, HiXMark } from "react-icons/hi2";

import classes from "./AlertModal.module.css";
import { alertActions } from "../../store/alert-slice";

const AUTO_DISMISS_MS = {
  success: 1800,
  error: 2800,
};

const AlertModal = () => {
  const dispatch = useDispatch();
  const showModal = useSelector((state) => state.alert.showModal);
  const message = useSelector((state) => state.alert.message);
  const status = useSelector((state) => state.alert.status);
  const overlayRoot = document.getElementById("overlay-root");

  const closeModalHandler = () => {
    dispatch(alertActions.resetState());
  };

  useEffect(() => {
    if (!showModal) return;
    const ms =
      status === "error" ? AUTO_DISMISS_MS.error : AUTO_DISMISS_MS.success;
    const id = window.setTimeout(() => {
      dispatch(alertActions.resetState());
    }, ms);
    return () => window.clearTimeout(id);
  }, [showModal, message, status, dispatch]);

  const tone = status === "success" ? classes.success : classes.error;
  const StatusIcon =
    status === "success" ? HiCheckCircle : HiExclamationTriangle;

  return ReactDOM.createPortal(
    <AnimatePresence>
      {showModal && (
        <motion.div
          key="admin-alert"
          className={classes.shell}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            className={classes.backdrop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            aria-hidden
          />
          <motion.div
            className={`${classes.panel} ${tone}`}
            role="alertdialog"
            aria-live="polite"
            initial={{ opacity: 0, y: -28, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -16, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 380, damping: 32 }}
          >
            <div className={classes.panelHead}>
              <StatusIcon className={classes.panelIcon} aria-hidden />
              <h2>{message}</h2>
            </div>
            <button
              type="button"
              className={classes.close}
              onClick={closeModalHandler}
            >
              <HiXMark className={classes.closeIcon} aria-hidden />
              Close
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    overlayRoot
  );
};

export default AlertModal;
